import directusServer from "./directus/directusGraphQLInstance"
import { rethrowIfDatabaseUnavailable } from "./errors/database"

// Note: previously typed with `LandingPageType` from '@/types/getHome', which
// no longer exists after a refactor. This loader is only consumed by its own
// test file, so we leave it untyped to avoid a build break.
type LandingPageType = Record<string, unknown>

const POST_FIELDS = `
  posts(
    sort: "-date_created"
    filter: { status: { _eq: "published" } }
    limit: 12
  ) {
    id
    slug
    date_created
    title
    headline
    featured
    role
    evidence_metric
    read_time
    image {
      id
      filename_download
      width
      height
    }
    category(
      filter: { category_id: { status: { _eq: "published" } } }
    ) {
      category_id {
        id
        title
        slug
        description
        icon_color
        color
        image {
          id
          filename_download
          width
          height
        }
      }
    }
    skills(
      filter: { skills_id: { status: { _eq: "published" } } }
    ) {
      skills_id(sort: "slug") {
        id
        slug
        name
        svg_logo
        svg_color_icon
        description
        area
        icon_color
      }
    }
    content_blocks {
      sort
      item {
        __typename
        ... on project_overview {
          client {
            name
            url
            svg_logo
          }
          headline
          hook
          platform
          industry
          team
        }
        ... on outcome {
          title
          headline
          description
          values
        }
      }
    }
  }
`

const COMPANY_FIELDS = `
  companies(
    filter: {
      is_education: { _eq: false }
      active: { _eq: true }
      featured_homepage: { _eq: true }
    }
  ) {
    id
    is_education
    featured_homepage
    url
    svg_logo
    svg_logo_black
    name
  }
`

const CV_FILE_FIELD = `
  cv_file {
    id
    title
    filename_download
  }
`

const SAFE_WEBPAGE_CONFIG_FIELDS = `
  location
  ${CV_FILE_FIELD}
`

const OPTIONAL_LANDING_WEBPAGE_CONFIG_FIELDS = [
  "badge_text",
  "headline",
  "headline_highlight",
  "intro",
  "primary_cta_label",
  "primary_cta_url",
  "secondary_cta_label",
  "trusted_label",
  "work_heading",
  "work_range",
  "how_i_work_heading",
  "method_items",
  "writing_heading",
  "writing_cta_label",
  "writing_cta_url",
  "final_cta_title",
  "final_cta_button_label",
  "final_cta_button_url",
]

const WEBPAGE_CONFIG_SCHEMA_QUERY = `
  query LandingWebpageConfigSchema {
    __type(name: "webpage_config") {
      fields {
        name
      }
    }
  }
`

interface WebpageConfigSchemaResponse {
  __type?: {
    fields?: { name: string }[]
  } | null
}

const buildLandingPageQuery = (webpageConfigFields: string) => `
  query getLandingPage {
    webpage_config {
      ${webpageConfigFields}
    }
    ${COMPANY_FIELDS}
    ${POST_FIELDS}
  }
`

const getWebpageConfigFields = async () => {
  try {
    const response = await directusServer.query<WebpageConfigSchemaResponse>(
      WEBPAGE_CONFIG_SCHEMA_QUERY
    )
    const availableFields = new Set(
      response.__type?.fields?.map((field) => field.name) ?? []
    )
    const landingFields = OPTIONAL_LANDING_WEBPAGE_CONFIG_FIELDS.filter(
      (field) => availableFields.has(field)
    )

    const baseFields = [
      availableFields.has("id") ? "id" : null,
      availableFields.has("admin_email") ? "admin_email" : null,
      availableFields.has("location") ? "location" : null,
      availableFields.has("cv_file") ? CV_FILE_FIELD : null,
    ].filter((field): field is string => Boolean(field))

    return `
      ${baseFields.join("\n")}
      ${landingFields.join("\n")}
    `
  } catch {
    return SAFE_WEBPAGE_CONFIG_FIELDS
  }
}

const getLandingPage = async () => {
  try {
    const webpageConfigFields = await getWebpageConfigFields()

    return await directusServer.query<LandingPageType>(
      buildLandingPageQuery(webpageConfigFields)
    )
  } catch (error) {
    rethrowIfDatabaseUnavailable(error)
    console.error(error)
    return null
  }
}

export default getLandingPage
